import { isEmpty } from 'lodash';
import tmsApi from './../api/tmsApi';
import helper from './helper';

const { createInputParam } = helper;

export default {
  namespaced: true,
  state: {
    prestarts: [],
    query: {
      search: '',
      company_id: null,
      type_filter_or: [],
      page: {
        lastData: null,
        count: 10,
      },
      sort: {
        by: 'ID',
        direction: 'ASC',
      },
    },
    detailDataId: null,
    detailData: null,
    errors: {
      actions: [],
    },
  },
  mutations: {
    CLEAR_DATA(state) {
      state.prestarts.splice(0);
    },
    ADD_DATA_ON_LIST(state, data) {
      state.prestarts.push(data);
    },
    DELETE_DETAIL_DATA(state, id) {
      const index = state.prestarts.findIndex(prestart => prestart.id === id);
      state.prestarts.splice(index, 1);
    },
    ADD_ERROR(state, action) {
      state.errors.actions.push(action);
    },
    SET_COMPANY_ID(state, id) {
      state.query.company_id = id;
    },
    CLEAR_QUERY({ query }) {
      query.search = '';
      query.company_id = null;
      query.type_filter_or = [];
      query.page.lastData = null;
      query.page.count = 10;
      query.sort.by = 'ID';
      query.sort.direction = 'ASC';
    },
    SET_QUERY_LAST_DATA({ query }, data) {
      query.page.lastData = data;
    },
    SET_TYPE_FILTER(state, types) {
      state.query.type_filter_or.splice(0);
      types.forEach((type) => {
        state.query.type_filter_or.push(type);
      });
    },
    SET_QUERY_SORT(state, sort) {
      const [by, direction] = sort.split('.');
      state.query.sort.direction = direction;
      state.query.sort.by = by;
    },
    SET_QUERY_SEARCH(state, search) {
      state.query.search = search;
    },
    SET_DETAIL_ID(state, id) {
      state.detailDataId = id;
    },
    SET_DETAIL_DATA(state, data) {
      state.detailData = data;
    },
  },
  getters: {
    prestarts(state) {
      return state.prestarts;
    },
    get_detail_data(state) {
      return state.detailData || {};
    },
  },
  actions: {
    LOAD_DATA({ commit, state, rootState }) {
      const api = tmsApi.buildRetry(rootState.token);
      const companyId = createInputParam(state.query, 'company_id', false);
      const { lastData, count } = state.query.page;
      const { by, direction } = state.query.sort;
      const typeFilter = state.query.type_filter_or;
      const limit = count;
      let after = '';
      let before = '';
      let skip = '';

      const typeValue = !isEmpty(typeFilter) ?
        `type_filter_or: [${typeFilter.join(', ')}]` : '';

      if (direction === 'ASC') {
        after = `after: "${lastData}"`;
      } else {
        before = `before: "${lastData}"`;
      }
      if (lastData !== null) {
        skip = `
          skip: {
            by: ID
            ${after}
            ${before}
          },
        `;
      }
      const query = `
        {
          prestarts(
            limit: ${limit},
            sort: {
              by: ${by},
              direction: ${direction}
            },
            ${skip},
            ${companyId}
            ${typeValue}
          ) {
            id
            name
            question
            type
            error_text
            metadata
          }
        }
      `;
      commit('CLEAR_DATA');
      return api(query, 'prestarts').then(({ data }) => {
        const { prestarts } = data;
        prestarts.forEach((prestart) => {
          commit('ADD_DATA_ON_LIST', prestart);
        });
      });
    },
    FIND_DATA({ commit, state, rootState }) {
      const api = tmsApi.buildRetry(rootState.token);
      const companyId = createInputParam(state.query, 'company_id', false);
      const { lastData, count } = state.query.page;
      const { by, direction } = state.query.sort;
      const typeFilter = state.query.type_filter_or;
      const limit = count;
      let after = '';
      let before = '';
      let skip = '';

      const typeValue = !isEmpty(typeFilter) ?
        `type_filter_or: [${typeFilter.join(', ')}]` : '';

      if (direction === 'ASC') {
        after = `after: "${lastData}"`;
      } else {
        before = `before: "${lastData}"`;
      }
      if (lastData !== null) {
        skip = `
          skip: {
            by: ID
            ${after}
            ${before}
          },
        `;
      }
      const query = `
        {
          prestarts: find_prestarts(
            query: "${state.query.search}",
            limit: ${limit},
            sort: {
              by: ${by},
              direction: ${direction}
            },
            ${skip},
            ${companyId}
            ${typeValue}
          ) {
            id
            name
            question
            type
            error_text
            metadata
          }
        }
      `;
      commit('CLEAR_DATA');
      return api(query, 'prestarts').then(({ data }) => {
        const { prestarts } = data;
        prestarts.forEach((prestart) => {
          commit('ADD_DATA_ON_LIST', prestart);
        });
      });
    },
    DELETE_DATA({ commit, rootState }, prestartId) {
      const api = tmsApi.build(rootState.token);
      const mutation = `mutation {
        deleted: delete_prestart(id: ${prestartId})
      }`;

      return api(mutation, 'delete_prestart').then(({ data }) => {
        const deleteOk = data.deleted;
        if (deleteOk) commit('DELETE_DETAIL_DATA', prestartId);
        return deleteOk;
      }).catch(() => {
        commit('ADD_ERROR', 'DELETE_DATA');
      });
    },
    LOAD_DETAIL_DATA({ state, commit, rootState }) {
      const api = tmsApi.buildRetry(rootState.token);
      const query = `{
        prestart(id: ${state.detailDataId}) {
          id
          name
          question
          type
          required
          error_text
          metadata
          company {
            id
            name
          }
        }
      }`;
      return api(query, 'prestart').then(({ data }) => {
        commit('SET_DETAIL_DATA', data.prestart);
      }).catch(() => {
        commit('ADD_ERROR', 'LOAD_DETAIL_DATA');
      });
    },
    CREATE_DATA({ commit, rootState }, payloads) {
      const { token } = rootState;
      const api = tmsApi.build(token);

      const {
        name,
        question,
        type,
        required,
        error_text,
        metadata,
        company,
      } = payloads;

      const query = `
        mutation CreateUpdatePrestart($input: PrestartCreateInput) {
          prestart: create_prestart(input: $input) {
            id
            name
            question
            type
            error_text
            required
            metadata
            company {
              id
              name
            }
          }
        }
      `;
      const variables = {
        input: {
          company_id: company.id,
          name,
          question,
          type,
          required,
          error_text,
          metadata,
        },
      };

      return api({
        query,
        variables,
      }, 'create_prestart').then(({ data }) => {
        commit('SET_DETAIL_DATA', data.prestart);
        return data.prestart;
      }).catch(() => {
        commit('ADD_ERROR', 'CREATE_DATA');
        return false;
      });
    },
    UPDATE_DATA({ commit, rootState, state }, payloads) {
      const { detailDataId: id } = state;
      const { token } = rootState;
      const api = tmsApi.build(token);

      const query = `
        mutation CreateUpdatePrestart($id: ID!, $input: PrestartUpdateInput) {
          prestart: update_prestart(id: $id, input: $input) {
            id
            name
            question
            type
            error_text
            required
            metadata
            company {
              id
              name
            }
          }
        }
      `;

      const {
        name,
        question,
        type,
        required,
        error_text,
        metadata,
        company,
      } = payloads;

      const variables = {
        id,
        input: {
          company_id: company.id,
          name,
          question,
          type,
          required,
          error_text,
          metadata,
        },
      };

      return api({
        query,
        variables,
      }, 'update_prestart').then(({ data }) => {
        commit('SET_DETAIL_DATA', data.prestart);
        return data.prestart;
      }).catch(() => {
        commit('ADD_ERROR', 'UPDATE_DATA');
        return false;
      });
    },
    LOAD_PRESTART_VEHICLE({ rootState }, payloads) {
      const api = tmsApi.buildRetry(rootState.token);
      const { vehicle_id } = payloads;
      const query = `
      {
        prestarts: get_vehicle_prestart_checklist(vehicle_id: ${vehicle_id}) {
          id
          name
          question
          type
          required
          error_text
          metadata
          company{
            id
            name
          }
        }
      }`;
      return api(query, 'vehicle_prestarts').then(({ data }) => {
        const { prestarts } = data;
        return prestarts;
      });
    },
    LOAD_DATA_BY_COMPANY({ rootState }, payloads) {
      const api = tmsApi.buildRetry(rootState.token);
      const { company_id } = payloads;
      const query = `
      {
        prestarts(company_id: ${company_id}) {
          id
          name
          question
          type
          required
          error_text
          metadata
          company{
            id
            name
          }
        }
      }`;
      return api(query, 'company_prestarts').then(({ data }) => {
        const { prestarts } = data;
        return prestarts;
      });
    },
    ASSIGN_PRESTART_VEHICLE({ rootState }, payloads) {
      const api = tmsApi.build(rootState.token);
      const { vehicle_id, prestart_checklist_ids } = payloads;
      const query = `
      mutation {
        assign: assign_multiple_prestart_vehicle(
          vehicle_id: ${vehicle_id},
          prestart_checklist_ids: [${prestart_checklist_ids}]
        )
      }`;
      return api(query, 'assign_vehicle_prestarts').then(({ data }) => {
        const { assign } = data;
        return assign;
      });
    },
    REMOVE_ASSIGN_PRESTART({ commit, rootState }, payloads) {
      const api = tmsApi.build(rootState.token);
      const { vehicle_id: vehicleId, prestart_checklist_id: prestartId } = payloads;
      const mutation = `mutation {
        deleted: remove_assign_prestart(vehicle_id: ${vehicleId}, prestart_checklist_id: ${prestartId})
      }`;

      return api(mutation, 'remove_assign_prestart').then(({ data }) => {
        const deleteOk = data.deleted;
        if (deleteOk) commit('DELETE_DETAIL_DATA', prestartId);
        return deleteOk;
      }).catch(() => {
        commit('ADD_ERROR', 'DELETE_DATA');
      });
    },
    COPY_ASSIGNED_PRESTART({ commit, rootState }, payloads) {
      const api = tmsApi.build(rootState.token);
      const { vehicle_id: vehicleId, vehicles_target: vehicleTargetIds } = payloads;
      const mutation = `mutation {
        copied: copy_assigned_prestart(vehicle_id: ${vehicleId}, vehicles_target: [${vehicleTargetIds}])
      }`;

      return api(mutation, 'copy_assigned_prestart').then(({ data }) => {
        const copyOk = data.copied;
        return copyOk;
      }).catch(() => {
        commit('ADD_ERROR', 'COPY_DATA');
      });
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/prestart_checklist.js